export interface SimActiveInfo {
  track: string;
  scenario: string;
  speed: number;
  seed: number;
  startedAt: string;
  expiresAt: string;
}

export interface SimStatus {
  running: boolean;
  info: SimActiveInfo | null;
  carAlive: boolean;
  tracks: string[];
  scenarios: string[];
}

export interface SimStartRequest {
  track: string;
  scenario: string;
  speed?: number;
  seed?: number;
  maxMinutes?: number;
}

/**
 * Parse the retained `{prefix}sim/active` payload. An empty payload (the
 * server clears the retained flag on stop) or anything that is not a
 * well-formed SimActiveInfo object means the simulator is not running.
 */
export function parseSimActiveInfo(payload: string): SimActiveInfo | null {
  if (!payload) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(payload);
  } catch {
    return null;
  }
  if (typeof parsed !== "object" || parsed === null) return null;
  const obj = parsed as Record<string, unknown>;
  if (
    typeof obj.track !== "string" ||
    typeof obj.scenario !== "string" ||
    typeof obj.speed !== "number" ||
    typeof obj.startedAt !== "string" ||
    typeof obj.expiresAt !== "string"
  ) {
    return null;
  }
  return {
    track: obj.track,
    scenario: obj.scenario,
    speed: obj.speed,
    seed: typeof obj.seed === "number" ? obj.seed : 0,
    startedAt: obj.startedAt,
    expiresAt: obj.expiresAt,
  };
}

export function formatTrackName(id: string): string {
  return id
    .split(/[-_]/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

export function formatSimSpeed(speed: number): string {
  return `${speed}×`;
}

export function formatClockTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

export function formatTimeRemaining(expiresAt: string, now: number = Date.now()): string {
  const end = new Date(expiresAt).getTime();
  if (Number.isNaN(end)) return "—";
  const remainingMs = end - now;
  if (remainingMs <= 0) return "expired";
  const totalMinutes = Math.ceil(remainingMs / 60_000);
  if (totalMinutes < 1) return "<1m";
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
}
